import React, { useState } from 'react';
import { Block, BlockViewState } from '../../types';
import { useBlockViewStore } from '../../block-view-store';
import { findChildren } from '@tiptap/core';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { TextSelection } from 'prosemirror-state';

const CountInput = ({
  value,
  onIncrease,
  onDecrease,
}: {
  value: number;
  onIncrease: () => void;
  onDecrease: () => void;
}) => {
  return (
    <div className='flex rounded-lg bg-neue-journey-fg-10 items-center w-[112px] px-3 py-2.5'>
      <div className='flex gap-2 items-center cursor-pointer' onClick={onDecrease}>
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M12.7992 8H3.19922'
            stroke='currentColor'
            strokeWidth='1.1'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </div>
      <span className='text-neue-journey-small text-neue-journey-fg text-center select-none w-full'>{value}</span>
      <div className='flex gap-2 items-center cursor-pointer' onClick={onIncrease}>
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M8.54922 2.99995C8.54922 2.69619 8.30298 2.44995 7.99922 2.44995C7.69546 2.44995 7.44922 2.69619 7.44922 2.99995V7.44995H2.99922C2.69546 7.44995 2.44922 7.69619 2.44922 7.99995C2.44922 8.30371 2.69546 8.54995 2.99922 8.54995H7.44922V13C7.44922 13.3037 7.69546 13.55 7.99922 13.55C8.30298 13.55 8.54922 13.3037 8.54922 13V8.54995H12.9992C13.303 8.54995 13.5492 8.30371 13.5492 7.99995C13.5492 7.69619 13.303 7.44995 12.9992 7.44995H8.54922V2.99995Z'
            fill='currentColor'
          />
        </svg>
      </div>
    </div>
  );
};

function getTableNode(blockViewState?: BlockViewState) {
  if (!blockViewState || blockViewState.type !== 'table' || !blockViewState.editor) {
    return null;
  }
  const { editor } = blockViewState;
  const results = findChildren(editor.state.doc, (node) => {
    return node.type.name === 'table';
  });
  if (results.length === 0) {
    return null;
  }
  const tableNodeWithPos = results[0];
  return tableNodeWithPos;
}

type Props = {
  block: Block;
};

export const TableControlPanelContent = ({ block }: Props) => {
  const [rows, setRows] = useState(0);
  const [columns, setColumns] = useState(0);

  const blockId = block.id;

  const blockViewState = useBlockViewStore((state) => state.blockViewStateMap[blockId]);

  const fetchTableState = useRefCallback(() => {
    const tableNode = getTableNode(blockViewState);
    if (!tableNode) {
      return;
    }
    const rows = tableNode.node.childCount;
    setRows(rows);
    const columns = tableNode.node.firstChild?.childCount ?? 0;
    setColumns(columns);
  }, [blockViewState]);

  React.useEffect(() => {
    fetchTableState();
  }, [blockViewState]);

  const onIncreaseRows = () => {
    const editor = blockViewState?.editor;
    if (!editor) {
      return;
    }
    const tableNode = getTableNode(blockViewState);
    if (!tableNode) {
      return;
    }
    const tableRows = findChildren(tableNode.node, (node) => {
      return node.type.name === 'tableRow';
    });
    const lastRow = tableRows[tableRows.length - 1];
    console.log('TableEditor', 'lastRow', lastRow.pos + lastRow.node.nodeSize, tableNode.pos + tableNode.node.nodeSize);
    const lastRowPos = lastRow.pos + lastRow.node.nodeSize;

    const transaction = editor.view.state.tr.setSelection(new TextSelection(editor.view.state.doc.resolve(lastRowPos)));
    editor.view.dispatch(transaction);
    editor.commands.addRowAfter();
    fetchTableState();
  };

  const onDecreaseRows = () => {
    const editor = blockViewState?.editor;
    if (!editor) {
      return;
    }
    const tableNode = getTableNode(blockViewState);
    if (!tableNode) {
      return;
    }
    const tableRows = findChildren(tableNode.node, (node) => {
      return node.type.name === 'tableRow';
    });
    if (!tableRows || tableRows.length === 0) {
      return;
    }
    const lastRow = tableRows[tableRows.length - 1];
    console.log('TableEditor', 'lastRow', lastRow.pos + lastRow.node.nodeSize, tableNode.pos + tableNode.node.nodeSize);
    const lastRowPos = lastRow.pos + lastRow.node.nodeSize;

    const transaction = editor.view.state.tr.setSelection(new TextSelection(editor.view.state.doc.resolve(lastRowPos)));
    editor.view.dispatch(transaction);
    editor.commands.deleteRow();
    fetchTableState();
  };

  const onIncreaseColumns = () => {
    const editor = blockViewState?.editor;
    if (!editor) {
      return;
    }
    const tableNode = getTableNode(blockViewState);
    if (!tableNode) {
      return;
    }
    const tableRows = findChildren(tableNode.node, (node) => {
      return node.type.name === 'tableRow';
    });
    const firstRow = tableRows[0];
    const firstRowColumns = findChildren(firstRow.node, (node) => {
      return node.type.name === 'tableCell';
    });
    const lastColumn = firstRowColumns[firstRowColumns.length - 1];
    const lastColumnPos = lastColumn.pos + lastColumn.node.nodeSize;

    const transaction = editor.view.state.tr.setSelection(
      new TextSelection(editor.view.state.doc.resolve(lastColumnPos))
    );
    editor.view.dispatch(transaction);
    editor.commands.addColumnAfter();
    fetchTableState();
  };

  const onDecreaseColumns = () => {
    const editor = blockViewState?.editor;
    if (!editor) {
      return;
    }
    const tableNode = getTableNode(blockViewState);
    if (!tableNode) {
      return;
    }
    const tableRows = findChildren(tableNode.node, (node) => {
      return node.type.name === 'tableRow';
    });
    const firstRow = tableRows[0];
    const firstRowColumns = findChildren(firstRow.node, (node) => {
      return node.type.name === 'tableCell';
    });
    const lastColumn = firstRowColumns[firstRowColumns.length - 1];
    const lastColumnPos = lastColumn.pos + lastColumn.node.nodeSize;

    const transaction = editor.view.state.tr.setSelection(
      new TextSelection(editor.view.state.doc.resolve(lastColumnPos))
    );
    editor.view.dispatch(transaction);
    editor.commands.deleteColumn();
    fetchTableState();
  };

  if (!blockViewState) {
    return null;
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center gap-4 justify-between'>
        <span className='text-neue-journey-medium select-none'>Rows</span>
        <CountInput value={rows} onIncrease={onIncreaseRows} onDecrease={onDecreaseRows} />
      </div>
      <div className='flex items-center gap-4 justify-between'>
        <span className='text-neue-journey-medium select-none'>Columns</span>
        <CountInput value={columns} onIncrease={onIncreaseColumns} onDecrease={onDecreaseColumns} />
      </div>
    </div>
  );
};
